
import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryResult } from '../types';

interface UseQueryOptions {
  enabled?: boolean;
  refetchOnMount?: boolean;
  staleTime?: number;
}

export function useQuery<T>(
  queryKey: string[],
  queryFn: () => Promise<T>,
  options: UseQueryOptions = {}
): QueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryFnRef = useRef(queryFn);
  const queryKeyRef = useRef(JSON.stringify(queryKey));

  const { enabled = true, refetchOnMount = true, staleTime = 5 * 60 * 1000 } = options;

  // Update refs when dependencies change
  queryFnRef.current = queryFn;

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    try {
      setIsLoading(true);
      setError(null);
      const result = await queryFnRef.current();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const currentQueryKey = JSON.stringify(queryKey);
    
    // Only fetch if query key changed or it's the initial mount
    if (refetchOnMount && (currentQueryKey !== queryKeyRef.current || data === null)) {
      queryKeyRef.current = currentQueryKey;
      fetchData();
    }
  }, [queryKey, refetchOnMount, fetchData, data]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
}
